<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row>
        <v-col cols="12">
          <v-card-title class="text-h4 pa-0">
            <v-btn icon @click="$router.back()" class="mr-3">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            Add New Client
          </v-card-title>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <v-form ref="form">
        <!-- Status Selector with Color -->
        <v-row>
          <v-col cols="12" md="2" class="ml-auto">
            <v-select :items="emp_status" v-model="formData.status" item-text="status_name" item-value="id"
              label="Status" outlined dense hide-details>
              <template v-slot:selection="{ item }">
                <v-btn :color="item.id === 'A' ? 'success' : 'error'" dark small block>
                  {{ item.status_name }}
                </v-btn>
              </template>
              <template v-slot:item="{ item }">
                <v-btn :color="item.id === 'A' ? 'success' : 'error'" dark small class="my-1" block>
                  {{ item.status_name }}
                </v-btn>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <!-- Primary Client Information -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="d-flex align-center">
              <span class="text-h6">Primary Client Information</span>
              <v-chip color="error" small class="ml-2">Required</v-chip>
              <v-radio-group v-model="formData.customer_type" row hide-details class="ml-4"
                @change="onCustomerTypeChange">
                <v-radio label="Individual Client" value="Individual"></v-radio>
                <v-radio label="Business Client" value="Business"></v-radio>
              </v-radio-group>
            </div>
          </v-col>
        </v-row>

        <!-- Individual Type Fields -->
        <template v-if="isIndividual">
          <!-- Row 1 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_name" label="Client Name" outlined dense required hide-details
                :rules="[(v) => !!v || 'Client Name is required']">
                <template v-slot:label>
                  <span>Client Name <span class="error--text">*</span></span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_email" label="Email" outlined dense hide-details
                type="email"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_contact" label="Contact Number" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 2 -->
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="formData.primary_address" label="Address" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_province" label="Province" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 3 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_country" label="Country" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_postcode" label="Postcode" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>
        </template>

        <!-- Business Type Fields (เหมือนเดิม) -->
        <template v-else>
          <!-- Row 1 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_name" label="Client Name" outlined dense required hide-details
                :rules="[(v) => !!v || 'Client Name is required']">
                <template v-slot:label>
                  <span>Client Name <span class="error--text">*</span></span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_abbreviation" label="Abbreviation" outlined dense hide-details
                :maxlength="10"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select :items="company_list" v-model="formData.primary_company_id" item-value="id" item-text="name"
                label="Choose Company" outlined dense hide-details disabled></v-select>
            </v-col>
          </v-row>

          <!-- Row 2 -->
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="formData.primary_address" label="Address" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_province" label="Province" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 3 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_country" label="Country" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_postcode" label="Postcode" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_email" label="Email" outlined dense hide-details
                type="email"></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 4 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_tel" label="Telephone Number" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_contact" label="Contact Number" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_mobile" label="Mobile Number" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 5 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.primary_fax" label="Fax" outlined dense hide-details></v-text-field>
            </v-col>
          </v-row>
        </template>

        <v-divider class="my-6"></v-divider>

        <!-- Secondary Client Information -->
        <v-row>
          <v-col cols="12">
            <span class="text-h6">Secondary Client Information</span>
          </v-col>
        </v-row>

        <!-- Individual Type - Secondary Fields (3 rows) -->
        <template v-if="isIndividual">
          <!-- Row 1 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_name" label="Client Name" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_email" label="Email" outlined dense hide-details
                type="email"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_contact" label="Contact Number" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 2 -->
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="formData.secondary_address" label="Address" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_province" label="Province" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 3 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_country" label="Country" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_postcode" label="Postcode" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>
        </template>

        <!-- Business Type - Secondary Fields (5 rows เหมือนเดิม) -->
        <template v-else>
          <!-- Row 1 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_name" label="Client Name" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_abbreviation" label="Abbreviation" outlined dense hide-details
                :maxlength="10"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select :items="company_list" v-model="formData.secondary_company_id" item-value="id" item-text="name"
                label="Choose Company" outlined dense hide-details disabled></v-select>
            </v-col>
          </v-row>

          <!-- Row 2 -->
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="formData.secondary_address" label="Address" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_province" label="Province" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 3 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_country" label="Country" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_postcode" label="Postcode" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_email" label="Email" outlined dense hide-details
                type="email"></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 4 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_tel" label="Telephone Number" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_contact" label="Contact Number" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_mobile" label="Mobile Number" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 5 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="formData.secondary_fax" label="Fax" outlined dense hide-details></v-text-field>
            </v-col>
          </v-row>
        </template>

        <v-divider class="my-6"></v-divider>

        <!-- Invoice/Receipt Information -->
        <v-row>
          <v-col cols="12">
            <span class="text-h6">Invoice/Receipt Information</span>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-checkbox v-model="formData.same_address_as_primary" label="Same Address as Primary Client Information"
              hide-details @change="copyPrimaryAddress"></v-checkbox>
          </v-col>
        </v-row>

        <!-- Invoice - Row 1 -->
        <v-row>
          <v-col cols="12" md="8">
            <v-text-field v-model="formData.invoice_address" label="Address" outlined dense hide-details
              :disabled="formData.same_address_as_primary"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="formData.invoice_province" label="Province" outlined dense hide-details
              :disabled="formData.same_address_as_primary"></v-text-field>
          </v-col>
        </v-row>

        <!-- Invoice - Row 2 -->
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="formData.invoice_country" label="Country" outlined dense hide-details
              :disabled="formData.same_address_as_primary"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="formData.invoice_postcode" label="Postcode" outlined dense hide-details
              :disabled="formData.same_address_as_primary"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select :items="currency_list" v-model="formData.invoice_currency" item-text="curr" item-value="curr"
              label="Currency" outlined dense hide-details></v-select>
          </v-col>
        </v-row>

        <!-- Invoice - Row 3 (แสดงเฉพาะ Business) -->
        <template v-if="isBusiness">
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model="formData.invoice_tax_id" label="Tax ID" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="formData.invoice_branch" label="Branch" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="formData.invoice_contact_name" label="Contact Name" outlined dense
                hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="formData.invoice_contact_position" label="Contact Position" outlined dense
                hide-details></v-text-field>
            </v-col>
          </v-row>

          <!-- Invoice - Row 4 (แสดงเฉพาะ Business) -->
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model.number="formData.invoice_vat" label="Vat(%)" outlined dense hide-details
                type="number"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select :items="vat_type_list" v-model="formData.invoice_vat_type" item-value="id" item-text="name"
                label="Vat Type" outlined dense hide-details></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model.number="formData.invoice_payment_term" label="Payment Term(Day)" outlined dense
                hide-details type="number"></v-text-field>
            </v-col>
          </v-row>
        </template>

        <!-- Invoice - Payment Term (แสดงเฉพาะ Individual) -->
        <template v-else>
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model.number="formData.invoice_payment_term" label="Payment Term(Day)" outlined dense
                hide-details type="number"></v-text-field>
            </v-col>
          </v-row>
        </template>

        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-center">
            <v-btn color="#126a94" dark rounded class="mr-4" width="150" @click="save">
              Save
            </v-btn>
            <v-btn color="#b90004" dark rounded width="150" @click="$router.back()">
              Cancel
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
    </v-card>
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import api from "@/services/api";
import { server } from "@/services/constants";

export default {
  components: {
    SuccessDialog,
  },
  data() {
    return {
      dialogAdd: false,
      text_color: "text-h5 green--text text-center",
      title: "green",
      message: "green",
      company_list: [],

      formData: {
        status: "A",
        customer_type: "Individual",

        // Primary Client Information
        primary_name: "",
        primary_abbreviation: "",
        primary_company_id: localStorage.getItem(server.COMPANYID),
        primary_address: "",
        primary_province: "",
        primary_country: "",
        primary_postcode: "",
        primary_email: "",
        primary_tel: "",
        primary_contact: "",
        primary_mobile: "",
        primary_fax: "",

        // Secondary Client Information
        secondary_name: "",
        secondary_abbreviation: "",
        secondary_company_id: Number(localStorage.getItem(server.COMPANYID)),
        secondary_address: "",
        secondary_province: "",
        secondary_country: "",
        secondary_postcode: "",
        secondary_email: "",
        secondary_tel: "",
        secondary_contact: "",
        secondary_mobile: "",
        secondary_fax: "",

        // Invoice/Receipt Information
        same_address_as_primary: false,
        invoice_address: "",
        invoice_province: "",
        invoice_country: "",
        invoice_postcode: "",
        invoice_currency: "THB",
        invoice_tax_id: "",
        invoice_branch: "",
        invoice_contact_name: "",
        invoice_contact_position: "",
        invoice_vat: 0,
        invoice_vat_type: "E",
        invoice_payment_term: 0,
      },

      emp_status: [
        { id: "A", status_name: "Active" },
        { id: "D", status_name: "Inactive" },
      ],

      vat_type_list: [
        { id: "I", name: "Include Vat" },
        { id: "E", name: "Exclude Vat" },
      ],

      company: [],
      currency_list: [],
    };
  },
  async mounted() {
    await this.loadCompanyMaster(localStorage.getItem(server.COMPANYID));
    await this.loadCurrency();
    // แก้ไขตรงนี้ - แปลงเป็น number
    const companyId = Number(localStorage.getItem(server.COMPANYID));
    this.formData.primary_company_id = companyId;
    this.formData.secondary_company_id = companyId;

    this.$hideLoader();
  },
  computed: {
    isIndividual() {
      return this.formData.customer_type === "Individual";
    },
    isBusiness() {
      return this.formData.customer_type === "Business";
    },
  },
  methods: {
    async loadCompanyMaster(id) {
      const result = await api.getCompanyByID(id);
      this.company_list = result.data;
      // console.log(`company_list: `, JSON.stringify(this.company_list));

      // this.company = result.data;
    },
    async loadCurrency() {
      const res_cur = await api.getDataCurrency();
      this.currency_list = res_cur.data;
    },

    onCustomerTypeChange() {
      this.formData.primary_company_id = `${localStorage.getItem(server.COMPANYID)}`;
      this.formData.secondary_company_id = `${localStorage.getItem(server.COMPANYID)}`;
      // ถ้าเปลี่ยนเป็น Individual ให้ล้างค่า fields ที่ไม่ใช้
      if (this.formData.customer_type === "Individual") {
        this.formData.primary_abbreviation = "";
        this.formData.primary_tel = "";
        this.formData.primary_mobile = "";
        this.formData.primary_fax = "";

        // ล้างข้อมูล Secondary ทั้งหมด
        this.formData.secondary_name = "";
        this.formData.secondary_abbreviation = "";
        this.formData.secondary_address = "";
        this.formData.secondary_province = "";
        this.formData.secondary_country = "";
        this.formData.secondary_postcode = "";
        this.formData.secondary_email = "";
        this.formData.secondary_tel = "";
        this.formData.secondary_contact = "";
        this.formData.secondary_mobile = "";
        this.formData.secondary_fax = "";
      }
    },

    copyPrimaryAddress() {
      if (this.formData.same_address_as_primary) {
        this.formData.invoice_address = this.formData.primary_address;
        this.formData.invoice_province = this.formData.primary_province;
        this.formData.invoice_country = this.formData.primary_country;
        this.formData.invoice_postcode = this.formData.primary_postcode;
      }
    },

    async save() {
      // Validate Primary Client Name (Required)
      if (this.formData.primary_name == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Failed",
          "Please enter Primary Client Name.",
          "text-h5 red--text text-center"
        );
        return;
      }

      // Check if name already exists
      const resChecKName = await api.getCustomerByName({
        params: {
          cus_name: this.formData.primary_name,
          company_id: localStorage.getItem(server.COMPANYID),
        },
      });

      if (resChecKName.data.length > 0) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Failed",
          "Primary Client Name already exists.",
          "text-h5 red--text text-center"
        );
        return;
      }

      // Check Document Running
      const resCheckDoc = await api.checkDocRunning(
        "Customer",
        localStorage.getItem(server.COMPANYID)
      );

      if (resCheckDoc.status == 200) {
        if (!resCheckDoc.data[0].resCheckDoc) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Information Saved",
            "Please kindly check the value of document running.",
            "text-h5 red--text text-center"
          );
          return;
        }
      } else {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Information Saved",
          "Cannot save data",
          "text-h5 red--text text-center"
        );
        return;
      }

      // Validation for Business Type
      if (this.formData.customer_type == "Business") {
        // Validate Postcode (Primary)
        if (this.formData.primary_postcode !== "") {
          if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.primary_postcode)) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Primary Postcode must be numbers only.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        // Validate Email (Primary)
        if (this.formData.primary_email !== "") {
          if (
            !/^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
              this.formData.primary_email
            )
          ) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Please check Primary Email format.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        // Validate Phone Numbers (Primary)
        if (this.formData.primary_tel !== "") {
          if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.primary_tel)) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Primary Telephone must be numbers only.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        if (this.formData.primary_contact !== "") {
          if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.primary_contact)) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Primary Contact Number must be numbers only.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        if (this.formData.primary_mobile !== "") {
          if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.primary_mobile)) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Primary Mobile Number must be numbers only.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        // Validate Invoice Postcode
        if (this.formData.invoice_postcode !== "") {
          if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.invoice_postcode)) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Invoice Postcode must be numbers only.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        // Check Abbreviation Duplicate (Primary)
        if (
          this.formData.primary_abbreviation != "" &&
          this.formData.primary_abbreviation != null
        ) {
          const res_c = await api.getCustomerAbb(
            this.formData.primary_abbreviation
          );
          if (res_c.data[0].status_abb > 0) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Primary Abbreviation already exists.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }

        // Check Abbreviation Duplicate (Secondary)
        if (
          this.formData.secondary_abbreviation != "" &&
          this.formData.secondary_abbreviation != null
        ) {
          const res_c = await api.getCustomerAbb(
            this.formData.secondary_abbreviation
          );
          if (res_c.data[0].status_abb > 0) {
            this.$store.state.global_dialog = true;
            this.setupAlertDialog(
              true,
              "Failed",
              "Secondary Abbreviation already exists.",
              "text-h5 red--text text-center"
            );
            return;
          }
        }
      }

      // Validation for Secondary fields (if filled)
      if (this.formData.secondary_postcode !== "") {
        if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.secondary_postcode)) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Failed",
            "Secondary Postcode must be numbers only.",
            "text-h5 red--text text-center"
          );
          return;
        }
      }

      if (this.formData.secondary_email !== "") {
        if (
          !/^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            this.formData.secondary_email
          )
        ) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Failed",
            "Please check Secondary Email format.",
            "text-h5 red--text text-center"
          );
          return;
        }
      }

      if (this.formData.secondary_tel !== "") {
        if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.secondary_tel)) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Failed",
            "Secondary Telephone must be numbers only.",
            "text-h5 red--text text-center"
          );
          return;
        }
      }

      if (this.formData.secondary_contact !== "") {
        if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.secondary_contact)) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Failed",
            "Secondary Contact Number must be numbers only.",
            "text-h5 red--text text-center"
          );
          return;
        }
      }

      if (this.formData.secondary_mobile !== "") {
        if (!/^([+-]*[0-9]+[+-]*)+$/.test(this.formData.secondary_mobile)) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Failed",
            "Secondary Mobile Number must be numbers only.",
            "text-h5 red--text text-center"
          );
          return;
        }
      }

      // Show Loading
      //   let loader = this.$loading.show({
      //     color: "green",
      //     loader: "dots",
      //     container: this.fullPage ? null : this.$refs.formContainer,
      //     canCancel: false,
      //     width: 120,
      //     height: 120,
      //     backgroundColor: "#ffffff",
      //     opacity: 0.5,
      //     zIndex: 999,
      //   });

      // Prepare data for insert
      const insertData = {
        doc_company_id: Number(localStorage.getItem(server.COMPANYID)),
        customer_type: this.formData.customer_type,
        status: this.formData.status,

        // Primary Client Information
        name: this.formData.primary_name,
        abbreviation: this.formData.primary_abbreviation
          ? this.formData.primary_abbreviation.toUpperCase()
          : "",
        company_id: Number(localStorage.getItem(server.COMPANYID)),
        address: this.formData.primary_address,
        provice: this.formData.primary_province,
        country: this.formData.primary_country,
        zipcode: this.formData.primary_postcode,
        email: this.formData.primary_email,
        tel: this.formData.primary_tel,
        contact: this.formData.primary_contact,
        tel_mobile: this.formData.primary_mobile,
        fax: this.formData.primary_fax,

        // Secondary Client Information
        secondary_name: this.formData.secondary_name,
        secondary_abbreviation: this.formData.secondary_abbreviation
          ? this.formData.secondary_abbreviation.toUpperCase()
          : "",
        secondary_company_id: this.formData.secondary_company_id,
        secondary_address: this.formData.secondary_address,
        secondary_province: this.formData.secondary_province,
        secondary_country: this.formData.secondary_country,
        secondary_postcode: this.formData.secondary_postcode,
        secondary_email: this.formData.secondary_email,
        secondary_tel: this.formData.secondary_tel,
        secondary_contact: this.formData.secondary_contact,
        secondary_mobile: this.formData.secondary_mobile,
        secondary_fax: this.formData.secondary_fax,

        // Invoice/Receipt Information
        address_invoice: this.formData.invoice_address,
        province_invoice: this.formData.invoice_province,
        country_invoice: this.formData.invoice_country,
        zipcode_invoice: this.formData.invoice_postcode,
        currency: this.formData.invoice_currency,
        taxid: this.formData.invoice_tax_id,
        branch: this.formData.invoice_branch,
        contact_name: this.formData.invoice_contact_name,
        contact_position: this.formData.invoice_contact_position,
        vat: this.formData.invoice_vat || 0,
        vat_type: this.formData.invoice_vat_type,
        payment_term: this.formData.invoice_payment_term || 0,

        user_create: localStorage.getItem(server.USER_ID),
      };

      try {
        this.$showLoader();
        const res_add = await api.addCustomer(insertData);
        // console.log(`res_add: ${JSON.stringify(res_add)}`);

        this.$hideLoader();
        if (res_add.status == 200 || res_add.status == 201) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Success",
            "Add data success",
            "text-h5 green--text text-center"
          );

          // Redirect back to list page
          setTimeout(() => {
            this.$router.push("/customer");
          }, 1500);

          return;
        } else {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Failed",
            res_add.data.message,
            "text-h5 red--text text-center"
          );
          return;
        }
      } catch (error) {
        this.$hideLoader();
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Failed",
          "Error saving data",
          "text-h5 red--text text-center"
        );
      }
    },

    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
  },
};
</script>

<style scoped>
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
